import { useState, useEffect } from "react";
import { Check, X, AlertCircle, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ParsedTaskFields } from "@/hooks/useVoiceCapture";
import { cn } from "@/lib/utils";
import { useRolePreview } from "@/contexts/RolePreviewContext";
import { fetchJobsForDropdown } from "@/hooks/useJobsQuery";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface VoiceTaskConfirmationProps {
  transcript: string;
  parsedFields: ParsedTaskFields;
  logId: string;
  contextJobId?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const VoiceTaskConfirmation = ({
  transcript,
  parsedFields,
  logId,
  contextJobId,
  onConfirm,
  onCancel,
}: VoiceTaskConfirmationProps) => {
  
  const [isSaving, setIsSaving] = useState(false);
  const [fields, setFields] = useState(parsedFields);
  const [jobs, setJobs] = useState<Array<{ id: string; name: string; job_number: string | null }>>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const { previewRole, isInPreviewMode } = useRolePreview();
  
  const effectiveRoles: AppRole[] = isInPreviewMode && previewRole 
    ? [previewRole as AppRole] 
    : userRoles;

  useEffect(() => {
    fetchUserRoles();
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [effectiveRoles]);

  const fetchUserRoles = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);
    
    setUserRoles(roles?.map(r => r.role as AppRole) || []);
  };

  const fetchJobs = async () => {
    const { data } = await fetchJobsForDropdown(effectiveRoles);
    if (data) setJobs(data);
  };

  const fetchReferenceData = async () => {
    const [peopleRes, vendorsRes] = await Promise.all([
      supabase.from("people").select("id, name").order("name"),
      supabase.from("vendors").select("id, name").order("name"),
    ]);

    if (peopleRes.data) setPeople(peopleRes.data);
    if (vendorsRes.data) setVendors(vendorsRes.data);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!fields.job_id && !contextJobId) {
      toast.error("Please select a job before saving");
      return;
    }

    setIsSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create task
      const taskData: any = {
        title: fields.title,
        details: fields.details,
        lane: fields.lane,
        job_id: fields.job_id || contextJobId,
        assignee_id: fields.assignee_id || user.id,
        status: "Open",
        notes: `Voice captured: "${transcript}"`,
      };

      if (fields.vendor_id) taskData.vendor_id = fields.vendor_id;
      if (fields.qty) taskData.qty = fields.qty;
      if (fields.units) taskData.units = fields.units;
      if (fields.due_date) taskData.due_date = fields.due_date;

      const { data: task, error: taskError } = await supabase
        .from("tasks")
        .insert(taskData)
        .select()
        .single();

      if (taskError) throw taskError;

      // Update voice log with task_id
      await supabase
        .from("voice_intake_log")
        .update({ task_id: task.id })
        .eq("id", logId);

      toast.success(`Task created in ${fields.lane} lane`);
      onConfirm();
    } catch (error: any) {
      console.error("Error creating task:", error);
      toast.error(error.message || "Failed to create task");
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (key: keyof ParsedTaskFields, value: any) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const getLaneBadgeColor = (lane: string) => {
    switch (lane) {
      case "Purchase":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20";
      case "Bring":
        return "bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/20";
      case "To-Do":
        return "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20";
      default:
        return "";
    }
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <Card className="w-full max-w-2xl max-h-[85vh] overflow-auto shadow-2xl border-2">
        <CardHeader className="bg-primary text-primary-foreground">
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Voice Task Confirmation
          </CardTitle>
          <CardDescription className="text-primary-foreground/80">
            Review and edit before saving
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6 space-y-4">
          {/* Transcript */}
          <div className="bg-muted/50 p-4 rounded-lg border">
            <Label className="text-xs text-muted-foreground">TRANSCRIPT</Label>
            <p className="text-sm mt-1 italic">"{transcript}"</p>
          </div>

          {/* Category Selection */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <div className="flex gap-2">
              {(["Purchase", "Bring", "To-Do"] as const).map((lane) => (
                <Badge
                  key={lane}
                  variant={fields.lane === lane ? "default" : "outline"}
                  className={cn(
                    "cursor-pointer px-4 py-2 text-sm transition-all",
                    fields.lane === lane ? getLaneBadgeColor(lane) : "hover:bg-muted"
                  )}
                  onClick={() => updateField("lane", lane)}
                >
                  {lane}
                </Badge>
              ))}
            </div>
          </div>

          {/* Job Selection - Required if not in context */}
          {!contextJobId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Job *
                {fields.needs_job_selection && (
                  <AlertCircle className="h-4 w-4 text-warning" />
                )}
              </Label>
              <Select
                value={fields.job_id || ""}
                onValueChange={(value) => updateField("job_id", value)}
              >
                <SelectTrigger className={cn(!fields.job_id && "border-warning")}>
                  <SelectValue placeholder="Select a job..." />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={fields.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="Task title"
            />
          </div>

          {/* Details */}
          <div className="space-y-2">
            <Label>Details</Label>
            <Textarea
              value={fields.details}
              onChange={(e) => updateField("details", e.target.value)}
              placeholder="Additional details..."
              rows={2}
            />
          </div>

          {/* Assignee */}
          <div className="space-y-2">
            <Label>Assigned To</Label>
            <Select
              value={fields.assignee_id || ""}
              onValueChange={(value) => updateField("assignee_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Assign to..." />
              </SelectTrigger>
              <SelectContent>
                {people.map((person) => (
                  <SelectItem key={person.id} value={person.id}>
                    {person.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Vendor - for Purchase/Bring */}
          {(fields.lane === "Purchase" || fields.lane === "Bring") && (
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Select
                value={fields.vendor_id || ""}
                onValueChange={(value) => updateField("vendor_id", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor..." />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Quantity & Units */}
          {(fields.lane === "Purchase" || fields.lane === "Bring") && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={fields.qty || ""}
                  onChange={(e) => updateField("qty", parseFloat(e.target.value))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Units</Label>
                <Input
                  value={fields.units || ""}
                  onChange={(e) => updateField("units", e.target.value)}
                  placeholder="ea, sheets, bags..."
                />
              </div>
            </div>
          )}

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={fields.due_date || ""}
              onChange={(e) => updateField("due_date", e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={isSaving || (!fields.job_id && !contextJobId)}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Check className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : "Save Task"}
            </Button>
            <Button onClick={onCancel} variant="outline" className="flex-1">
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
